import React, { useState, useEffect } from 'react';
import { ui } from '../../../config/uiSizes';

interface ReplacePrefixParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

/**
 * One-step prefix swap (DEMO-5). Collects the old prefix to strip and the new prefix to add. This is
 * sent to the backend as a first-class `ReplacePrefix` transformation (an atomic old→new swap that
 * also rewrites flow published names/references consistently) — it is no longer expanded into a
 * RemovePrefix + AddPrefix pair, which produced `UAT-TEST-Flow1` on flows.
 */
export const ReplacePrefixParameters: React.FC<ReplacePrefixParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [oldPrefix, setOldPrefix] = useState<string>((parameters?.['oldPrefix'] as string) || '');
  const [newPrefix, setNewPrefix] = useState<string>((parameters?.['newPrefix'] as string) || '');

  // The target name attribute is resolved per resource type on the backend (e.g. a Script's
  // name lives in `script_name`, journey types use `display_name`), so the user only supplies
  // the prefixes — never a single property name that could not be correct for every selected type.
  useEffect(() => {
    onParametersChange?.({ oldPrefix, newPrefix });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldPrefix, newPrefix]);

  return (
    <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
      <div className={`${ui.mb.title}`}>
        <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>Replace Prefix</h3>
        <p className={`${ui.text.label} text-gray-600 dark:text-gray-400 mt-1`}>
          Swap one prefix for another in a single step (removes the old prefix, then adds the new one).
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Old Prefix (to remove) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={oldPrefix}
            onChange={(e) => setOldPrefix(e.target.value)}
            placeholder="TST_"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={` ${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Names starting with this prefix have it stripped. Examples: "TST_", "DEV_".
          </p>
        </div>

        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            New Prefix (to add) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newPrefix}
            onChange={(e) => setNewPrefix(e.target.value)}
            placeholder="QA_"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={` ${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Prepended after the old prefix is removed. Examples: "QA_", "PROD_".
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReplacePrefixParameters;
